import { useLocale } from 'use-intl';
import { useTranslations } from '@/i18n/translations';

import { DEFAULT_MAX_OUTPUT_PX } from './ImageCropDialog';

const TYPE_LABELS: Record<string, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
};

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

// Mirrors ImageCropDialog's exportCrop: the longer side is capped at
// `maxOutputPx`, so that is the largest render the upload can ever produce —
// anything bigger is downscaled, anything smaller is upscaled (blurry).
export function recommendedCropSize(
  aspect: number,
  maxOutputPx: number,
): { width: number; height: number; ratio: string } {
  const width = aspect >= 1 ? maxOutputPx : Math.round(maxOutputPx * aspect);
  const height = aspect >= 1 ? Math.round(maxOutputPx / aspect) : maxOutputPx;
  const divisor = gcd(width, height);
  return { width, height, ratio: `${width / divisor}:${height / divisor}` };
}

export function useImageUploadHint({
  aspect,
  maxOutputPx = DEFAULT_MAX_OUTPUT_PX,
  contentTypes,
  maxBytes,
}: {
  aspect: number;
  maxOutputPx?: number;
  contentTypes: ReadonlyArray<string>;
  maxBytes: number;
}): string {
  const t = useTranslations('Common');
  const locale = useLocale();
  const { width, height, ratio } = recommendedCropSize(aspect, maxOutputPx);
  const types = new Intl.ListFormat(locale, { type: 'disjunction' }).format(
    contentTypes.map((type) => TYPE_LABELS[type] ?? type),
  );
  return t('imageUploadHint', {
    width: String(width),
    height: String(height),
    ratio,
    types,
    maxMb: String(Math.round(maxBytes / (1024 * 1024))),
  });
}
